<template>
  <div class="flex items-center gap-6">
    <div
      class="w-32 h-32 rounded-full flex items-center justify-center shrink-0"
      :style="donutStyle"
    >
      <div class="w-[78px] h-[78px] rounded-full bg-white flex flex-col items-center justify-center">
        <div class="text-xl font-semibold leading-none tabular-nums text-gray-dark">
          {{ total }}
        </div>
        <div class="text-[10px] text-slate-500">
          porteurs
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <div>
        <div class="flex items-center gap-2 text-sm text-gray-dark">
          <span class="w-3 h-3 rounded-sm" :style="{ background: LOYALTY_CARD_GENDER_COLORS.female }" />
          Femmes
        </div>
        <div class="text-xl font-semibold tabular-nums ml-5 leading-tight">
          {{ femalePercentage }}%
        </div>
      </div>
      <div>
        <div class="flex items-center gap-2 text-sm text-gray-dark">
          <span class="w-3 h-3 rounded-sm" :style="{ background: LOYALTY_CARD_GENDER_COLORS.male }" />
          Hommes
        </div>
        <div class="text-xl font-semibold tabular-nums ml-5 leading-tight">
          {{ malePercentage }}%
        </div>
      </div>
      <div v-if="unknownPercentage > 0">
        <div class="flex items-center gap-2 text-sm text-gray-dark">
          <span class="w-3 h-3 rounded-sm" :style="{ background: LOYALTY_CARD_GENDER_COLORS.unknown }" />
          Non renseigné
        </div>
        <div class="text-xl font-semibold tabular-nums ml-5 leading-tight">
          {{ unknownPercentage }}%
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from "vue";
import {LOYALTY_CARD_GENDER_COLORS} from "@/loyalty-card/constants";

const props = defineProps<{
    /** Number of male members. */
    male: number;
    /** Number of female members. */
    female: number;
    /** Total members (denominator for the percentages). */
    total: number;
}>();

const femalePercentage = computed(() => (
    props.total > 0 ? Math.round((props.female / props.total) * 100) : 0
));

const malePercentage = computed(() => (
    props.total > 0 ? Math.round((props.male / props.total) * 100) : 0
));

const unknownPercentage = computed(() => (
    props.total > 0
        ? Math.max(0, Math.round(((props.total - props.male - props.female) / props.total) * 100))
        : 0
));

/**
 * Conic gradient: female arc, male arc, then a neutral remainder for
 * members whose gender is unknown — so the arcs match the legend.
 */
const donutStyle = computed(() => {
    const femaleShare = props.total > 0 ? (props.female / props.total) * 100 : 0;
    const maleEnd = props.total > 0
        ? Math.min(100, femaleShare + (props.male / props.total) * 100)
        : 0;

    return {
        background: `conic-gradient(${LOYALTY_CARD_GENDER_COLORS.female} 0 ${femaleShare}%, ${LOYALTY_CARD_GENDER_COLORS.male} ${femaleShare}% ${maleEnd}%, ${LOYALTY_CARD_GENDER_COLORS.unknown} ${maleEnd}% 100%)`
    };
});
</script>
